import { KeyboardEvent, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDisclosure } from '@chakra-ui/react';
import { sortBy } from 'lodash';

import useFilterProvider from '../../HOCs/Filter/useFilterProvider';
import { getQuestionAnswers } from '../../utils/functions/display-survey.utils';

import { SharedExpandedViewModal } from './SharedExpandedViewModal';

/**
 * This modal is displayed on click on expand a question in surveys results page.
 * It is handled via a hash-based URL navigation (#expandedq${questionId}) and supports navigation between questions.
 */
export const QuestionResultsModal = ({
  dataTablesDisplayStates,
  toggleDataTableDisplay,
  editDisplayStates,
  toggleEditDisplay,
  displayPrevAvg,
  togglePrevAvgDisplay,
  hideFilterBar,
}: {
  dataTablesDisplayStates?: Record<string, boolean>;
  toggleDataTableDisplay?: (questionId: string) => void;
  editDisplayStates?: Record<string, boolean>;
  toggleEditDisplay?: (questionId: string) => void;
  displayPrevAvg?: boolean;
  togglePrevAvgDisplay?: () => void;
  hideFilterBar?: boolean;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { engagementDetails, engagementResponses, setBreakoutDemographic } =
    useFilterProvider();

  const questionIdFromUrl = location.hash?.startsWith('#expandedq')
    ? location.hash.replace('#expandedq', '')
    : null;

  const sortedQuestions = sortBy(engagementDetails.questions, 'position');

  const currentQuestionIndex = useMemo(
    () => sortedQuestions.findIndex((q) => q.id === questionIdFromUrl),
    [sortedQuestions, questionIdFromUrl]
  );

  const canGoPrevious = currentQuestionIndex > 0;
  const canGoNext = currentQuestionIndex < sortedQuestions.length - 1;

  const currentQuestion =
    currentQuestionIndex >= 0
      ? sortedQuestions[currentQuestionIndex]
      : sortedQuestions[0];

  const currentQuestionAnswers = useMemo(
    () => getQuestionAnswers(engagementResponses, currentQuestion.id),
    [engagementResponses, currentQuestion]
  );

  const { isOpen, onOpen, onClose: onDisclosureClose } = useDisclosure();

  // Open modal if hash in the URL
  useEffect(() => {
    if (questionIdFromUrl && currentQuestion) {
      onOpen();
    } else {
      onDisclosureClose();
    }
  }, [questionIdFromUrl, currentQuestion, onOpen, onDisclosureClose]);

  // Removes the hash from URL on close
  const onClose = () => {
    navigate({ search: searchParams.toString(), hash: '' });
    setBreakoutDemographic(undefined);
  };

  // Navigation between questions
  const goToQuestionAtIndex = (index: number) => {
    const newQuestion = sortedQuestions[index];
    navigate({
      search: searchParams.toString(),
      hash: `expandedq${newQuestion.id}`,
    });
  };

  const goPrevious = () => {
    if (canGoPrevious) goToQuestionAtIndex(currentQuestionIndex - 1);
  };

  const goNext = () => {
    if (canGoNext) goToQuestionAtIndex(currentQuestionIndex + 1);
  };

  const navigateQuestionsWithKeys = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrevious();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  };

  return (
    <SharedExpandedViewModal
      isOpen={isOpen}
      onClose={onClose}
      currentQuestion={currentQuestion}
      currentQuestionAnswers={currentQuestionAnswers}
      displayPrevAvg={displayPrevAvg}
      togglePrevAvgDisplay={togglePrevAvgDisplay}
      hideFilterBar={hideFilterBar}
      goPrevious={goPrevious}
      goNext={goNext}
      canGoPrevious={canGoPrevious}
      canGoNext={canGoNext}
      navigateQuestionsWithKeys={navigateQuestionsWithKeys}
      isControlled={false}
      displayDataTable={
        dataTablesDisplayStates && dataTablesDisplayStates[currentQuestion.id]
      }
      toggleDataTableDisplay={() =>
        toggleDataTableDisplay && toggleDataTableDisplay(currentQuestion.id)
      }
      displayEditToggle={
        editDisplayStates && editDisplayStates[currentQuestion.id]
      }
      toggleEditDisplay={() =>
        toggleEditDisplay && toggleEditDisplay(currentQuestion.id)
      }
    />
  );
};
